"use client";

import React, { useCallback, useState } from "react";
import { SendHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

export interface EntityCommentComposerProps {
  /** Called with trimmed text; parent performs create + error handling */
  onSubmit: (text: string) => Promise<void>;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Min rows for the textarea */
  minRows?: number;
  /** Use compact single-line input with Enter-to-submit */
  inline?: boolean;
}

/**
 * Reusable bottom-bar style composer for entity comments/notes.
 * Entity-specific hooks (e.g. useCreateTaskNote) live in the parent.
 */
export function EntityCommentComposer({
  onSubmit,
  placeholder = "Add a comment…",
  disabled = false,
  className,
  minRows = 2,
  inline = false,
}: EntityCommentComposerProps) {
  const [value, setValue] = useState("");
  const [sending, setSending] = useState(false);
  const [inlineMouseActive, setInlineMouseActive] = useState(false);

  const handleSubmit = useCallback(async () => {
    const text = value.trim();
    if (!text || sending || disabled) return;
    setSending(true);
    try {
      await onSubmit(text);
      setValue("");
    } finally {
      setSending(false);
    }
  }, [value, sending, disabled, onSubmit]);

  return (
    <div className={cn("flex flex-col gap-2 border-t border-gray-200 bg-card pt-3", className)}>
      <div className="flex gap-2 items-end">
        {inline ? (
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder}
            disabled={disabled || sending}
            className="h-10 text-sm flex-1"
            onMouseDown={() => setInlineMouseActive(true)}
            onBlur={() => setInlineMouseActive(false)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && inlineMouseActive) {
                e.preventDefault();
                void handleSubmit();
              }
            }}
          />
        ) : (
          <Textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder}
            disabled={disabled || sending}
            rows={minRows}
            className="min-h-[72px] resize-y text-sm flex-1"
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                void handleSubmit();
              }
            }}
          />
        )}
        <Button
          type="button"
          size="sm"
          className="shrink-0"
          disabled={disabled || sending || !value.trim()}
          onClick={() => void handleSubmit()}
        >
          <SendHorizontal className="h-4 w-4 mr-1" />
          Add
        </Button>
      </div>
    </div>
  );
}
